﻿/* https://brandplatform.smartphoto.com/general/popups/ai-multiple-filters/ */
import { IDisposable, compositeDisposable } from "../../utils/disposable";
import { showLoadingDialog } from "../popups/loading-dialog";
import { createPopup } from "../technical/popup-new";
import { expressEventListener, expressQuerySelector, expressQuerySelectorAll } from "../../common/html";
export interface IAiMultipleFiltersDependencies {
	close?: () => void;
	error?: (message: string) => void;
}

export function createAiMultipleFilters(
	deps: IAiMultipleFiltersDependencies,
	photoId: string,
	pvc: string,
	design: string,
) {
	const buttonDisabledClass = "a-button--disabled";
	const eventDisposables: IDisposable = compositeDisposable(undefined);
	let hasCurrentCreations = false;

	const init = async () => {
		const aiPhotoPopup = await showLoadingDialog(
			createPopup({
				dialogId: "showaiphotopopup_" + photoId,
				view: "PhotoWithAiFilter",
				closeOnClickOutsidePopup: false,
				onBeforeOpen: (popupEl: HTMLElement) => {
					// Load the main picture if it is pending
					const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", false);
					const loading = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main-loading", false);
					if (mainPhoto) {
						const originalPhotoId = mainPhoto.dataset.originalPhotoId;
						const originalPhotoUrl = mainPhoto.src;

						// Enable the click on the original photo in the history
						const historyEl = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-history", false);
						if (historyEl)
							eventDisposables.add(
								expressEventListener(historyEl, "click", () => {
									if (historyEl.dataset.photoid) {
										swapPhoto(historyEl.dataset.photoid, historyEl.dataset.photourl);
										const activeHistory = expressQuerySelector<HTMLImageElement>(
											popupEl,
											".p-ai-multiple-filters__history-container--active",
											false,
										);
										if (activeHistory)
											activeHistory.classList.remove("p-ai-multiple-filters__history-container--active");
										historyEl.classList.add("p-ai-multiple-filters__history-container--active");

										// Deselect any active filters
										const activeFilter = expressQuerySelector<HTMLImageElement>(
											popupEl,
											".technical-aifilter-filter.p-ai-multiple-filters__filter-container--active",
											false,
										);
										if (activeFilter) activeFilter.classList.remove("p-ai-multiple-filters__filter-container--active");
									}
								}),
							);

						// Enable the history clicks
						const historyContainer = expressQuerySelector<HTMLElement>(
							popupEl,
							".technical-aifilter-history-container",
							false,
						);
						if (historyContainer)
							eventDisposables.add(
								expressEventListener(historyContainer, "click", (e) => {
									const historyDiv = e.target.closest(".technical-aifilter-history");
									if (historyDiv && historyDiv.dataset.photoid) {
										swapPhoto(historyDiv.dataset.photoid, historyDiv.dataset.photourl);
										const activeHistory = expressQuerySelector<HTMLImageElement>(
											popupEl,
											".p-ai-multiple-filters__history-container--active",
											false,
										);

										// Select the clicked history as being active
										if (activeHistory)
											activeHistory.classList.remove("p-ai-multiple-filters__history-container--active");
										historyDiv.classList.add("p-ai-multiple-filters__history-container--active");

										// Match the clicked history with the clicked filter
										const activeFilter = expressQuerySelector<HTMLImageElement>(
											popupEl,
											".technical-aifilter-filter.p-ai-multiple-filters__filter-container--active",
											false,
										);
										if (activeFilter) activeFilter.classList.remove("p-ai-multiple-filters__filter-container--active");
										const matchingFilter = expressQuerySelector<HTMLImageElement>(
											popupEl,
											".technical-aifilter-filter[data-filterNumber='" + historyDiv.dataset.filternumber + "']",
											false,
										);
										if (matchingFilter) matchingFilter.classList.add("p-ai-multiple-filters__filter-container--active");

										// Add a divider between past creations and current ones
										if (hasCurrentCreations) {
											const divider = expressQuerySelector<HTMLImageElement>(
												popupEl,
												".technical-aifilter-history-divider",
												false,
											);
											if (divider) divider.classList.remove("u-hide");
										}
									}
								}),
							);

						// Enable the filter clicks
						const aiFilters = expressQuerySelectorAll<HTMLElement>(popupEl, ".technical-aifilter-filter");
						aiFilters.forEach((aiFilter) => {
							eventDisposables.add(
								expressEventListener(aiFilter, "click", () => {
									// Check if we already clicked this filter before. If yes? Select it from the history
									const existingHistory = expressQuerySelector<HTMLImageElement>(
										popupEl,
										".technical-aifilter-history[data-filterNumber='" + aiFilter.dataset.filternumber + "']",
										false,
									);
									if (existingHistory) {
										existingHistory.click();
										return;
									}

									hasCurrentCreations = true;

									// Update the label of the buttons
									const continueButtons = expressQuerySelectorAll<HTMLLinkElement>(
										popupEl,
										".technical-aifilter-continue",
									);
									continueButtons.forEach((continueButton) => {
										continueButton.textContent = continueButton.dataset.labelcontinue;
									});

									// Set the filter as being active
									aiFilters.forEach((otherAiFilter) => {
										otherAiFilter.classList.remove("p-ai-multiple-filters__filter-container--active");
									});
									aiFilter.classList.add("p-ai-multiple-filters__filter-container--active");

									/* History photos */
									// History: Make a copy of the template
									const historyEl = expressQuerySelector<HTMLImageElement>(
										popupEl,
										".technical-aifilter-history-template",
										false,
									);
									const newHistoryEl = historyEl.cloneNode(true) as HTMLImageElement;

									// History: Remove active class of other history items
									const otherActiveHistory = expressQuerySelector<HTMLElement>(
										popupEl,
										".technical-aifilter-history.p-ai-multiple-filters__history-container--active",
										false,
									);
									otherActiveHistory?.classList.remove("p-ai-multiple-filters__history-container--active");

									// History: Set attributes of the newly created history div
									newHistoryEl.dataset.filternumber = aiFilter.dataset.filternumber;
									newHistoryEl.classList.add("p-ai-multiple-filters__history-container--active");
									newHistoryEl.classList.remove("u-hide");
									newHistoryEl.classList.remove("technical-aifilter-history-template");
									newHistoryEl.removeAttribute("data-photoid");
									newHistoryEl.removeAttribute("data-photourl");

									// History: Set the image to loading of the new history element
									const historyImage = expressQuerySelector<HTMLImageElement>(newHistoryEl, "img", true);
									historyImage.classList.add("u-hide");
									const loadingEl = expressQuerySelector<HTMLElement>(newHistoryEl, ".technical-photo-loading");
									loadingEl.classList.remove("u-hide");

									// History: Add the item to the DOM
									const historyContainer = historyEl.parentElement;
									if (historyContainer) historyContainer.insertBefore(newHistoryEl, historyContainer.firstChild);

									// History: Add a divider between past creations and current ones
									const divider = expressQuerySelector<HTMLImageElement>(
										popupEl,
										".technical-aifilter-history-divider",
										false,
									);
									if (divider) divider.classList.remove("u-hide");

									fetch("/apicore/aifilter/starttask", {
										method: "POST",
										headers: {
											"Content-Type": "application/json",
										},
										body: JSON.stringify({
											originalPhotoId,
											pvc,
											chosenFilterGroupNumber: aiFilter.dataset.filternumber,
										}),
									})
										.then((response) => response.json())
										.then((data) => {
											// Change the main picture to the original one
											const pendingId = data.pendingId;
											mainPhoto.setAttribute("data-pendingid", pendingId);
											mainPhoto.src = originalPhotoUrl;
											loading.classList.remove("u-hide");

											// Disable the buttons
											const continueButtons = expressQuerySelectorAll<HTMLLinkElement>(
												popupEl,
												".technical-aifilter-continue",
											);
											continueButtons.forEach((continueButton) => {
												if (continueButton.href) {
													continueButton.classList.add(buttonDisabledClass);
													continueButton.setAttribute("data-href", continueButton.href);
													continueButton.removeAttribute("href");
												}
											});

											return new Promise<void>((resolve, reject) => {
												// Start the polling to change it to the final result
												const checkPending = () => {
													fetch("/apicore/aifilter/aipoll", {
														method: "POST",
														headers: {
															"Content-Type": "application/json",
														},
														body: JSON.stringify({ id: pendingId }),
													})
														.then((response) => response.json())
														.then((data) => {
															if (data && data.status === "done") {
																pendingDone(popupEl, pendingId, data.id, data.url, aiFilter.dataset.filternumber);
																resolve();
															} else if (data && data.status === "failed") {
																reject(data.message || "AI processing failed");
															} else {
																setTimeout(checkPending, 1500);
															}
														})
														.catch((error) => {
															reject(error);
														});
												};
												checkPending();
											});
										});
								}),
							);
						});

						const pendingDone = (
							popupEl: HTMLElement,
							pendingId: string,
							newPhotoId: string,
							photoUrl: string,
							filterNumber?: string,
						) => {
							// Show the main photo + change the CTA URL
							if (mainPhoto.dataset.pendingid == pendingId) swapPhoto(newPhotoId, photoUrl);

							// Show the history photo
							const historyEl = expressQuerySelector<HTMLImageElement>(
								popupEl,
								".technical-aifilter-history[data-filterNumber='" + filterNumber + "']",
								false,
							);
							if (historyEl) {
								const loadingEl = expressQuerySelector<HTMLElement>(historyEl, ".technical-photo-loading");
								const historyImage = expressQuerySelector<HTMLImageElement>(historyEl, "img", true);
								historyImage.classList.remove("u-hide");
								historyImage.src = photoUrl;
								historyEl.dataset.photoid = newPhotoId;
								historyEl.dataset.photourl = photoUrl;
								loadingEl.classList.add("u-hide");
							}
						};

						const swapPhoto = (photoId: string, photoUrl: string) => {
							mainPhoto.src = photoUrl;
							mainPhoto.removeAttribute("data-pendingid");

							const continueButtons = expressQuerySelectorAll<HTMLLinkElement>(popupEl, ".technical-aifilter-continue");
							continueButtons.forEach((continueButton) => {
								const currentHref = continueButton.getAttribute("href") || continueButton.dataset.href;
								const updatedHref = currentHref.replace(/photoids=[0-9a-f-]+/i, `photoids=${photoId}`);
								continueButton.setAttribute("href", updatedHref);
								continueButton.removeAttribute("data-href");
								continueButton.textContent = continueButton.dataset.labelcontinue;
								continueButton.classList.remove(buttonDisabledClass);
							});

							loading.classList.add("u-hide");
						};

						const pendingId = mainPhoto.dataset.pendingid || "";
						if (pendingId && pendingId !== "") {
							return new Promise<void>((resolve, reject) => {
								const checkPending = () => {
									fetch("/apicore/aifilter/aipoll", {
										method: "POST",
										headers: {
											"Content-Type": "application/json",
										},
										body: JSON.stringify({ id: pendingId }),
									})
										.then((response) => response.json())
										.then((data) => {
											if (data && data.status === "done") {
												pendingDone(popupEl, pendingId, data.id, data.url);
												resolve();
											} else if (data && data.status === "failed") {
												reject(data.message || "AI processing failed");
											} else {
												setTimeout(checkPending, 1500);
											}
										})
										.catch((error) => {
											reject(error);
										});
								};
								checkPending();
							});
						}
					}
				},
				forceRefresh: true, // refresh to reset api errors in popup
				data: {
					Pvc: pvc,
					Design: design,
					PhotoIds: photoId, // for now, only use the first photo
				},
			}),
		);

		await aiPhotoPopup.openAsync();

		if (aiPhotoPopup.popup.dataset.hasError) {
			if (aiPhotoPopup.popup.dataset.showOnPagelog) {
				if (deps.error) deps.error(aiPhotoPopup.popup.dataset.errorMessage);
				return;
			}
			// something went wrong => open select photo dialog again
			return Promise.reject(aiPhotoPopup.popup.dataset.errorMessage);
		}

		const continueBtns = expressQuerySelectorAll(aiPhotoPopup.popup, ".technical-aifilter-continue");
		continueBtns.forEach((continueBtn) => {
			eventDisposables.add(
				expressEventListener(continueBtn, "click", () => {
					if (continueBtn.classList.contains(buttonDisabledClass)) return;
					showLoadingDialog(Promise.resolve(), 0, 10000);
				}),
			);
		});
	};

	init();

	return {
		dispose: () => {
			eventDisposables.dispose();
		},
	};
}
